import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";

import FormInput from "components/FormInput";
import FormFileInput from "components/FormFileInput";
import FormSelect from "components/FormSelect";
import FormInDialog from "components/FormInDialog";

import categoryFormValidate from "screens/Categories/components/validate";
import { fetchClientsList } from "store/client/actions";

const CategoryModal = ({
  OpenerComponent,
  title,
  submitActionText,
  cancelActionText,
  initialValues,
  ...formProps
}) => {
  const dispatch = useDispatch();
  const { list: clientsList } = useSelector(({ clientReducer }) => clientReducer);
  const { authObject } = useSelector(({ authReducer }) => authReducer);

  // Determine if we're in edit mode (clientId is already set)
  const isEditMode = initialValues?.clientId && initialValues.clientId !== "";

  const allowedClients = useMemo(() => {
    if (authObject?.isAdmin) {
      return clientsList;
    }
    
    if (authObject?.clientRoles && authObject.clientRoles.length > 0) {
      const allowedClientIds = authObject.clientRoles.map(cr => 
        cr.client?._id || cr.client
      );
      
      const clientsWithAccess = clientsList.filter(client => 
        allowedClientIds.some(id => {
          const clientId = typeof client._id === 'string' ? client._id : client._id?.toString();
          const allowedId = typeof id === 'string' ? id : id?.toString();
          return clientId === allowedId;
        })
      );
      
      return clientsWithAccess.filter(client => {
        const clientId = client._id?.toString() || client._id;
        
        const clientRole = authObject.clientRoles.find(cr => {
          const crClientId = cr.client?._id?.toString() || cr.client?._id || cr.client;
          return crClientId === clientId;
        });
        
        if (!clientRole || !clientRole.roles || !Array.isArray(clientRole.roles)) {
          return false;
        }
        
        return clientRole.roles.some(roleId => {
          if (!authObject.roleActions || !authObject.roleActions[roleId]) {
            return false;
          }
          
          const roleActions = authObject.roleActions[roleId];
          const permission = 'CATEGORIES.CATEGORIES_CREATE';
          const keys = permission.split('.') || ['CATEGORIES', 'CATEGORIES_CREATE'];
          
          let permissions = roleActions;
          for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (key in permissions) {
              permissions = permissions[key];
            } else {
              return false;
            }
          }
          
          return permissions === true || permissions === 1;
        });
      });
    }
    
    return clientsList;
  }, [clientsList, authObject]);

  const clientsWithLabel = useMemo(() => {
    return allowedClients.map(client => ({
      ...client,
      displayLabel: `${client.code || 'N/A'} - ${client.name || 'No name'}`
    }));
  }, [allowedClients]);

  useEffect(() => {
    if (clientsList.length === 0) {
      dispatch(fetchClientsList());
    }
  }, [dispatch]);

  const categoryModalFields = [
    {
      name: "clientId",
      component: FormSelect,
      props: {
        label: "Client",
        required: true,
        requiredMessage: "Please select a client",
        fullWidth: true,
        variant: "outlined",
        options: clientsWithLabel,
        valueAccessor: "_id",
        labelAccessor: "displayLabel",
        placeholder: "Select a client",
        disabled: isEditMode,
      },
    },
    {
      name: "name",
      component: FormInput,
      props: {
        label: "Category name",
        required: true,
        fullWidth: true,
        variant: "outlined",
      },
    },
    {
      name: "pictures",
      component: FormFileInput,
      props: {
        placeholder: "",
        label: "Featured image",
        externalLabel: true,
        accept: "image/*",
      },
    },
  ];

  return (
    <FormInDialog
      validate={categoryFormValidate}
      OpenerComponent={(props) => <OpenerComponent {...props} />}
      title={title}
      submitActionText={submitActionText}
      cancelActionText={cancelActionText}
      formFields={categoryModalFields}
      initialValues={initialValues}
      {...formProps}
    />
  );
};

CategoryModal.propTypes = {
  OpenerComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  title: PropTypes.string.isRequired,
  submitActionText: PropTypes.string.isRequired,
  cancelActionText: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default CategoryModal;
